import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  updateProfile,
  UserCredential
} from 'firebase/auth';
import { auth } from '../firebase';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<UserCredential>;
  signInWithGoogleRedirect: () => Promise<void>;
  updateUserProfile: (displayName: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  return useContext(AuthContext) as AuthContextType;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function signUp(email: string, password: string) {
    if (!auth) {
      throw new Error("Firebase Auth is not initialized");
    }
    return createUserWithEmailAndPassword(auth, email, password)
      .then(() => {
        // Sign-up successful
      });
  }

  async function login(email: string, password: string) {
    if (!auth) {
      throw new Error("Firebase Auth is not initialized");
    }
    return signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        // Login successful
      });
  }

  async function logout() {
    if (!auth) {
      throw new Error("Firebase Auth is not initialized");
    }
    return signOut(auth);
  }

  async function resetPassword(email: string) {
    if (!auth) {
      throw new Error("Firebase Auth is not initialized");
    }
    return sendPasswordResetEmail(auth, email);
  }

  async function signInWithGoogle() {
    if (!auth) {
      throw new Error("Firebase Auth is not initialized");
    }
    console.log("Attempting Google sign-in...");
    const provider = new GoogleAuthProvider();
    
    // Add scopes if needed
    provider.addScope('https://www.googleapis.com/auth/userinfo.email');
    provider.addScope('https://www.googleapis.com/auth/userinfo.profile');
    
    // Set custom parameters
    provider.setCustomParameters({
      'login_hint': 'user@example.com',
      // Forces account selection even when one account is available
      'prompt': 'select_account'
    });
    
    try {
      console.log("Calling signInWithPopup...");
      const result = await signInWithPopup(auth, provider);
      
      // This gives you a Google Access Token. You can use it to access the Google API.
      // const credential = GoogleAuthProvider.credentialFromResult(result);
      // const token = credential?.accessToken;
      
      console.log("Google sign-in successful:", result.user.email);
      return result;
    } catch (error: any) {
      console.error("Google sign-in error details:", error);
      // Log specific error code if available
      if (error.code) {
        console.error("Error code:", error.code);
      }
      // Log message if available
      if (error.message) {
        console.error("Error message:", error.message);
      }
      throw error;
    }
  }

  async function signInWithGoogleRedirect() {
    if (!auth) {
      throw new Error("Firebase Auth is not initialized");
    }
    console.log("Attempting Google sign-in with redirect...");
    const provider = new GoogleAuthProvider();
    
    // Add scopes if needed
    provider.addScope('https://www.googleapis.com/auth/userinfo.email');
    provider.addScope('https://www.googleapis.com/auth/userinfo.profile');
    
    // Set custom parameters
    provider.setCustomParameters({
      // Forces account selection even when one account is available
      'prompt': 'select_account'
    });
    
    try {
      console.log("Calling signInWithRedirect...");
      await signInWithRedirect(auth, provider);
      // The page will redirect, so no need to return anything
    } catch (error: any) {
      console.error("Google sign-in redirect error details:", error);
      if (error.code) {
        console.error("Error code:", error.code);
      }
      if (error.message) {
        console.error("Error message:", error.message);
      }
      throw error;
    }
  }

  // Check for redirect result when component mounts
  useEffect(() => {
    async function checkRedirectResult() {
      if (!auth) {
        console.error("Firebase Auth is not initialized");
        return;
      }
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          // User successfully signed in after redirect
          console.log("Google sign-in redirect successful:", result.user.email);
        }
      } catch (error: any) {
        console.error("Error processing redirect result:", error);
      }
    }
    
    if (!loading) {
      checkRedirectResult();
    }
  }, [loading]);

  async function updateUserProfile(displayName: string) {
    if (currentUser) {
      return updateProfile(currentUser, {
        displayName: displayName
      });
    }
    return Promise.reject("No user is signed in");
  }

  useEffect(() => {
    // Check if auth is available
    if (!auth) {
      console.error("Firebase Auth is not available");
      setLoading(false);
      return () => {};
    }

    try {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        setCurrentUser(user);
        setLoading(false);
      }, (error) => {
        console.error("Auth state change error:", error);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error setting up auth state listener:", error);
      setLoading(false);
      return () => {};
    }
  }, []);

  const value = {
    currentUser,
    loading,
    signUp,
    login,
    logout,
    resetPassword,
    signInWithGoogle,
    signInWithGoogleRedirect,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 